import { FunctionComponent, ReactNode, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

interface TooltipProps {
	children: ReactNode;
	content: ReactNode | string;
}

/** Distance from the trigger, and the inset the arrow keeps from a rounded corner. */
const GAP = 8;
const ARROW_INSET = 14;

interface Placement {
	top: number;
	left: number;
	/** Where the tooltip ended up, so the arrow points back at the trigger. */
	side: "top" | "bottom";
	/** The arrow's offset within the tooltip — the trigger's centre, not the tooltip's. */
	arrowLeft: number;
}

/**
 * A tooltip that appears where it is pointed at, at once.
 *
 * Measured in a layout effect and held invisible until it has been: the position depends
 * on the rendered size, so there is one frame where it is not yet known. Painting during
 * that frame put the tooltip in the page's top-left corner and then moved it, which on a
 * quick hover was all anyone saw.
 *
 * Dark on purpose — §1 of the brand scale gives `ink` to tooltips, and a floating white
 * card over a white editor needs a border to exist at all, which is how the old one read.
 */
export const Tooltip: FunctionComponent<TooltipProps> = ({ children, content }) => {
	const [isVisible, setIsVisible] = useState(false);
	const [placement, setPlacement] = useState<Placement | null>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (!isVisible || !triggerRef.current || !tooltipRef.current) {
			return;
		}

		const trigger = triggerRef.current.getBoundingClientRect();
		const tooltip = tooltipRef.current.getBoundingClientRect();
		// Above by preference, below when there is no room — a tooltip clipped by the top of
		// the window says nothing at all.
		const side = trigger.top - tooltip.height - GAP > 0 ? "top" : "bottom";
		// Centred on the trigger, but kept inside the viewport: a wide tooltip on a control
		// near the edge would otherwise hang off the screen.
		// The left gap wins when the viewport is narrower than the tooltip plus both gaps:
		// clamping the other way round produced a negative left and pushed the box off the
		// screen's left edge on small windows.
		const left = Math.max(
			GAP,
			Math.min(
				trigger.left + trigger.width / 2 - tooltip.width / 2,
				window.innerWidth - tooltip.width - GAP
			)
		);

		setPlacement({
			side,
			top:
				(side === "top" ? trigger.top - tooltip.height - GAP : trigger.bottom + GAP) +
				window.scrollY,
			left: left + window.scrollX,
			// Follows the trigger even when the box was pushed sideways to fit, so the arrow
			// keeps pointing at what it describes rather than at the middle of the box.
			arrowLeft: Math.min(
				Math.max(ARROW_INSET, trigger.left + trigger.width / 2 - left),
				tooltip.width - ARROW_INSET
			),
		});
	}, [isVisible, content]);

	const hide = () => {
		setIsVisible(false);
		// Dropped so the next hover measures again rather than flashing at the last
		// position, which is wrong the moment anything on the page has moved.
		setPlacement(null);
	};

	return (
		<>
			<div
				ref={triggerRef}
				className="inline-flex"
				onBlur={hide}
				onFocus={() => setIsVisible(true)}
				onMouseEnter={() => setIsVisible(true)}
				onMouseLeave={hide}
			>
				{children}
			</div>
			{isVisible &&
				ReactDOM.createPortal(
					<div
						ref={tooltipRef}
						className={`absolute z-[150] max-w-[320px] rounded-lg bg-ink px-3 py-2 text-xs font-medium leading-relaxed text-white shadow-lg transition-opacity duration-150 motion-reduce:transition-none ${
							placement ? "opacity-100" : "invisible opacity-0"
						}`}
						role="tooltip"
						style={{ top: placement?.top ?? 0, left: placement?.left ?? 0 }}
					>
						{content}
						<span
							aria-hidden="true"
							className="absolute h-2 w-2 rotate-45 bg-ink"
							style={{
								left: (placement?.arrowLeft ?? 0) - 4,
								...(placement?.side === "bottom" ? { top: -4 } : { bottom: -4 }),
							}}
						/>
					</div>,
					document.body
				)}
		</>
	);
};
